import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/Breadcrumb";
import PageHeader from "@/components/PageHeader";
import { blogPosts, blogCategories } from "@/data/blogPosts";
import { cx } from "@/components/kit";

const ease = [0.22, 1, 0.36, 1];

const PostCard = ({ post, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.06, ease }}
  >
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group flex h-full flex-col rounded-[1.5rem] border border-ivory-400 bg-white overflow-hidden shadow-card-light hover:border-electric/40 hover:shadow-card-lift hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative h-44 bg-ivory-100 overflow-hidden">
        <img
          src={post.image}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain p-4 drop-shadow-[0_18px_28px_rgba(0,56,255,0.15)] transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 rounded-full bg-electric text-white text-[11px] font-semibold px-3 py-1">
          {post.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6 lg:p-7">
        <div className="flex items-center gap-3 text-xs text-coal/45 mb-3">
          <span>{post.date}</span>
          <span className="w-1 h-1 rounded-full bg-coal/20" />
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readingTime}</span>
        </div>
        <h2 className="font-display font-bold text-xl text-coal leading-snug mb-3 group-hover:text-electric transition-colors">
          {post.title}
        </h2>
        <p className="text-sm text-coal/60 leading-relaxed flex-1">{post.excerpt}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-coal group-hover:text-electric transition-colors">
          <span className="link-draw">Lees artikel</span>
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  </motion.article>
);

const BlogPage = () => {
  const [filter, setFilter] = useState("Alle");

  useSeo({
    title: "Kennisbank | SEO, AI Marketing & Online Groei Artikelen",
    description:
      "Praktische artikelen over SEO, AI-marketing, advertising en webdesign — geschreven door UPPIT. Geen theorie maar toepasbare kennis waarmee MKB & ZZP direct groeien.",
    path: "/blog",
    ogSlug: "page/blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "UPPIT Kennisbank",
      url: "https://uppit.nl/blog",
      inLanguage: "nl-NL",
      publisher: { "@type": "Organization", name: "UPPIT", url: "https://uppit.nl" },
      blogPost: blogPosts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `https://uppit.nl/blog/${p.slug}`,
        datePublished: p.dateISO,
        author: { "@type": "Person", name: "Tim Roosendaal" },
      })),
    },
  });

  const featured = blogPosts[0];
  const rest = filter === "Alle" ? blogPosts.slice(1) : blogPosts.filter((p) => p.category === filter);

  return (
    <main data-testid="blog-page" className="relative pt-28 lg:pt-32 pb-24 bg-ivory min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Kennisbank", href: "/blog" }]} />
        <PageHeader
          kicker="Kennisbank · Blog"
          italic="Slimmer groeien."
          bold="Kennis die omzet oplevert."
          lede="Geen theorie maar praktijk: artikelen over SEO, AI-marketing, advertising en webdesign die je vandaag kunt toepassen. Zo werken wij — en zo laten we het zien."
        />

        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-filters">
          {["Alle", ...blogCategories].map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`blog-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={cx(
                "px-5 py-2.5 rounded-full text-sm transition-colors duration-300",
                filter === cat
                  ? "bg-electric text-white font-semibold"
                  : "border border-ivory-400 bg-white text-coal/70 hover:border-electric/40 hover:text-electric"
              )}
            >
              {cat}
            </button>
          ))}
        </div>

        {filter === "Alle" && (
          <motion.article
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease }}
            className="mb-8"
          >
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured-card"
              className="group grid grid-cols-1 lg:grid-cols-12 rounded-[1.75rem] border border-ivory-400 bg-white overflow-hidden shadow-card-light hover:border-electric/40 hover:shadow-card-lift transition-all duration-300"
            >
              <div className="relative lg:col-span-5 min-h-[240px] bg-ivory-100 overflow-hidden">
                <img
                  src={featured.image}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-contain p-8 drop-shadow-[0_24px_40px_rgba(0,56,255,0.16)] transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-5 left-5 rounded-full bg-electric text-white text-[11px] font-semibold px-3 py-1">
                  Nieuwste · {featured.category}
                </span>
              </div>
              <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 text-xs text-coal/45 mb-4">
                  <span>{featured.date}</span>
                  <span className="w-1 h-1 rounded-full bg-coal/20" />
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.readingTime} leestijd</span>
                </div>
                <h2 className="font-display font-black text-2xl lg:text-4xl text-coal leading-tight tracking-tight mb-4 group-hover:text-electric transition-colors text-balance">
                  {featured.title}
                </h2>
                <p className="text-coal/60 leading-relaxed max-w-xl">{featured.excerpt}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-electric">
                  Lees het volledige artikel
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </Link>
          </motion.article>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {rest.map((post, i) => (
            <PostCard key={post.slug} post={post} index={i} />
          ))}
        </div>
      </div>
    </main>
  );
};

export default BlogPage;
